import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageFade, Reveal } from "@/components/site/motion";
import { PROFILE_PHOTO, SERVICES, EMAIL } from "@/lib/constants";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const SKILLS = [
  { name: "Graphic Design", desc: "Identity systems, editorial layout, campaign graphics", tags: ["Identity", "Print", "Campaign"] },
  { name: "Motion Design", desc: "Kinetic type, animated branding", tags: ["Kinetic Type", "Loops"] },
  { name: "Video Editing", desc: "Commercial cuts, short-form storytelling", tags: ["Commercial", "Short-Form", "Story"] },
  { name: "UI Design", desc: "Interfaces, landing pages, design systems", tags: ["Web", "Product", "Systems"] },
  { name: "Web Development", desc: "Fast, expressive websites and web experiences", tags: ["Websites", "Landing Pages", "CMS"] },
  { name: "Public Speaker", desc: "Design talks, workshops, community sessions", tags: ["Talks", "Workshops", "Events"] },
];

const EXPERIENCE = [
  { years: "2023 — 2026", role: "Versatile Designer", place: "Telkomsel" },
  { years: "2022 — 2023", role: "Graphic Designer & Video Editor", place: "MD Media" },
  { years: "2020 — 2021", role: "Web & Graphic Designer", place: "Noesantara Corp" },
];

const SOFTWARE = ["Photoshop", "Illustrator", "After Effects", "Premiere Pro", "Canva", "Capcut", "Figma", "Framer"];

export default function About() {
  useDocumentTitle("About — JARE");

  return (
    <PageFade className="pt-36">
      {/* Intro */}
      <section className="mx-auto grid w-[92%] max-w-7xl grid-cols-1 gap-14 pb-24 lg:grid-cols-12" data-testid="about-intro">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow mb-6">About</p>
            <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              A designer who thinks in systems and cuts like a filmmaker
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 max-w-xl space-y-5 text-base leading-relaxed text-neutral-400 md:text-lg">
              <p>
                Hi, I&apos;m JARE — a versatile designer &amp; creative professional with over
                5 years of experience bridging graphic design, video editing, web development,
                and public speaking.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <blockquote className="mt-10 border-l-2 border-white/25 pl-6 text-xl italic text-neutral-200 md:text-2xl">
              “An idea is just a spark; execution is the flame.”
            </blockquote>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="glass rounded-[2rem] p-3">
            <img
              src={PROFILE_PHOTO}
              alt="Portrait of JARE"
              className="aspect-[4/5] w-full rounded-[1.6rem] object-cover grayscale contrast-110"
              data-testid="about-profile-photo"
            />
          </div>
        </Reveal>
      </section>

      {/* Skills — editorial rows, no percentage bars */}
      <section className="border-t border-white/[0.08] bg-[#0B0B0B]/60 py-24" data-testid="about-skills">
        <div className="mx-auto w-[92%] max-w-7xl">
          <Reveal className="mb-12">
            <p className="eyebrow mb-5">Capabilities</p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Six disciplines, deeply held
            </h2>
          </Reveal>
          <div>
            {SKILLS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.05}>
                <div
                  className="group grid grid-cols-1 items-baseline gap-3 border-t border-white/[0.08] py-7 transition-all duration-500 hover:pl-4 md:grid-cols-12"
                  data-testid={`skill-row-${i}`}
                >
                  <span className="font-mono text-xs tracking-[0.3em] text-neutral-600 md:col-span-1">
                    0{i + 1}
                  </span>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-white transition-colors md:col-span-4 md:text-3xl">
                    {s.name}
                  </h3>
                  <p className="text-sm text-neutral-400 md:col-span-4">{s.desc}</p>
                  <div className="flex flex-wrap gap-2 md:col-span-3 md:justify-end">
                    {s.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-neutral-500 transition-colors group-hover:border-white/25 group-hover:text-neutral-300">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Experience + Software */}
      <section className="mx-auto grid w-[92%] max-w-7xl grid-cols-1 gap-16 py-24 lg:grid-cols-2">
        <div data-testid="about-experience">
          <Reveal>
            <p className="eyebrow mb-8">Experience</p>
          </Reveal>
          {EXPERIENCE.map((e, i) => (
            <Reveal key={e.years} delay={i * 0.07}>
              <div className="glass glass-hover mb-4 rounded-2xl p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">{e.years}</p>
                <h3 className="font-display mt-2 text-xl font-bold text-white">{e.role}</h3>
                <p className="mt-1 text-sm text-neutral-400">{e.place}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div data-testid="about-software">
          <Reveal>
            <p className="eyebrow mb-8">Software & Tools</p>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="glass rounded-3xl p-8">
              <div className="flex flex-wrap gap-3">
                {SOFTWARE.map((s) => (
                  <span
                    key={s}
                    className="rounded-full border border-white/12 bg-white/[0.03] px-5 py-2.5 text-sm text-neutral-200 transition-colors duration-300 hover:border-white/35 hover:text-white"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="glass mt-6 rounded-3xl p-8">
              <p className="eyebrow mb-5">Services</p>
              <ul className="space-y-3">
                {SERVICES.map((s) => (
                  <li key={s.n} className="flex items-center justify-between gap-4 text-sm">
                    <span className="font-medium text-white">{s.title}</span>
                    <span className="font-mono text-[11px] text-neutral-600">{s.n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact strip */}
      <section className="mx-auto w-[92%] max-w-7xl pb-28">
        <Reveal>
          <div className="glass glass-hover flex flex-col items-start justify-between gap-8 rounded-3xl p-10 md:flex-row md:items-center md:p-14">
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
                Like how this reads?
              </h2>
              <p className="mt-3 text-neutral-400">
                The next case study could be yours. <span className="text-neutral-200">{EMAIL}</span>
              </p>
            </div>
            <Link to="/contact" className="btn-primary" data-testid="about-cta">
              Start a Project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </PageFade>
  );
}
